/**
 * SEO configuration for mySTORYme public routes.
 * Site URL can be overridden via REACT_APP_SITE_URL at build time.
 */

export const SITE_URL = (
  process.env.REACT_APP_SITE_URL || 'https://mystoryme.com'
).replace(/\/$/, '');

export const SITE_NAME = 'mySTORYme';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

const defaultOg = {
  siteName: SITE_NAME,
  type: 'website',
  locale: 'en_US',
  image: DEFAULT_OG_IMAGE,
  imageWidth: '1200',
  imageHeight: '630',
  imageAlt:
    'A child discovering a magical personalized story where they are the hero.',
};

/** Routes that must not be indexed (private app, auth, admin). */
export const NOINDEX_PATH_PREFIXES = [
  '/account',
  '/profiles',
  '/stories',
  '/experience',
  '/admin',
];

export const NOINDEX_EXACT_PATHS = ['/login', '/register', '/forgot-password'];

export const isNoIndexRoute = (pathname) => {
  if (NOINDEX_EXACT_PATHS.includes(pathname)) return true;
  return NOINDEX_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
};

/** Public URLs included in sitemap.xml */
export const SITEMAP_PATHS = [
  '/',
  '/about',
  '/contact',
  '/privacy',
  '/terms',
  '/help',
  '/roadmap',
  '/feedback',
  '/pricing',
];

const homeSeo = {
  title: 'mySTORYme | Personalized Stories Where Your Child Is the Hero',
  description:
    'Create magical personalized stories for kids where your child becomes the hero. Add their name, interests, family, and friends to make storytime unforgettable.',
  robots: 'index, follow',
  og: {
    ...defaultOg,
    title: 'Personalized Stories Where Your Child Becomes the Hero',
    description:
      'Turn your child’s name, interests, family, and imagination into a magical story made just for them. Create a personalized kids’ story in minutes.',
  },
  twitter: {
    card: 'summary_large_image',
    title: 'Your Child as the Hero of Their Own Story',
    description:
      'Create magical personalized kids’ stories featuring your child’s name, interests, family, and imagination.',
  },
  jsonLd: {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: 'mySTORYme',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web',
    description:
      "AI-powered personalized children's story generator that creates custom bedtime stories featuring your child as the hero.",
    offers: {
      '@type': 'Offer',
      price: '6.99',
      priceCurrency: 'USD',
    },
  },
};

/** Per-route SEO; pathname -> config */
export const ROUTE_SEO = {
  '/': homeSeo,
  '/about': {
    title: 'About mySTORYme | Personalized Kids’ Stories',
    description:
      'Learn how mySTORYme helps families create personalized bedtime stories where children become the heroes of their own adventures.',
    robots: 'index, follow',
    og: {
      ...defaultOg,
      title: 'About mySTORYme',
      description:
        'Discover the story behind personalized kids’ stories built for magical family storytime.',
    },
    twitter: {
      card: 'summary_large_image',
      title: 'About mySTORYme',
      description:
        'Personalized kids’ stories where your child is the hero of every adventure.',
    },
  },
  '/contact': {
    title: 'Contact mySTORYme | We’re Here to Help',
    description:
      'Get in touch with the mySTORYme team for support, partnerships, privacy questions, and feedback.',
    robots: 'index, follow',
    og: {
      ...defaultOg,
      title: 'Contact mySTORYme',
      description: 'Reach our team for help with personalized stories and your account.',
    },
    twitter: {
      card: 'summary_large_image',
      title: 'Contact mySTORYme',
      description: 'Questions about personalized kids’ stories? Contact us today.',
    },
  },
  '/privacy': {
    title: 'Privacy Policy | mySTORYme',
    description:
      'Read how mySTORYme protects children’s data and family privacy when creating personalized stories.',
    robots: 'index, follow',
    og: {
      ...defaultOg,
      title: 'Privacy Policy | mySTORYme',
      description: 'Our commitment to privacy and safe storytelling for kids and families.',
    },
    twitter: {
      card: 'summary',
      title: 'Privacy Policy | mySTORYme',
      description: 'How we protect your family’s data and privacy.',
    },
  },
  '/terms': {
    title: 'Terms & Conditions | mySTORYme',
    description:
      'Terms and conditions for using mySTORYme to create personalized children’s stories.',
    robots: 'index, follow',
    og: {
      ...defaultOg,
      title: 'Terms & Conditions | mySTORYme',
      description: 'Terms of use for mySTORYme personalized story platform.',
    },
    twitter: {
      card: 'summary',
      title: 'Terms & Conditions | mySTORYme',
      description: 'Terms of use for mySTORYme.',
    },
  },
  '/help': {
    title: 'Help Center | mySTORYme',
    description:
      'Find answers about creating profiles, generating personalized kids’ stories, subscriptions, and more.',
    robots: 'index, follow',
    og: {
      ...defaultOg,
      title: 'Help Center | mySTORYme',
      description: 'Guides and answers for your mySTORYme personalized story journey.',
    },
    twitter: {
      card: 'summary_large_image',
      title: 'Help Center | mySTORYme',
      description: 'Support for personalized kids’ stories and your account.',
    },
  },
  '/roadmap': {
    title: 'Product Roadmap | mySTORYme',
    description:
      'See what’s coming next for mySTORYme — new features for personalized children’s storytelling.',
    robots: 'index, follow',
    og: {
      ...defaultOg,
      title: 'Product Roadmap | mySTORYme',
      description: 'Upcoming features for magical personalized kids’ stories.',
    },
    twitter: {
      card: 'summary_large_image',
      title: 'Roadmap | mySTORYme',
      description: 'What we’re building next for families and storytellers.',
    },
  },
  '/feedback': {
    title: 'Feedback | mySTORYme',
    description:
      'Share your ideas and feedback to help us improve personalized stories for kids and families.',
    robots: 'index, follow',
    og: {
      ...defaultOg,
      title: 'Feedback | mySTORYme',
      description: 'Tell us how we can make mySTORYme even more magical.',
    },
    twitter: {
      card: 'summary_large_image',
      title: 'Feedback | mySTORYme',
      description: 'Help shape the future of personalized kids’ stories.',
    },
  },
  '/pricing': {
    title: 'Pricing | mySTORYme Personalized Stories',
    description:
      'Choose a mySTORYme plan and start creating personalized kids’ stories where your child is the hero.',
    robots: 'index, follow',
    og: {
      ...defaultOg,
      title: 'Pricing | mySTORYme',
      description:
        'Affordable plans for unlimited magical personalized stories for your child.',
    },
    twitter: {
      card: 'summary_large_image',
      title: 'Pricing | mySTORYme',
      description: 'Plans for personalized kids’ stories starting at $6.99.',
    },
  },
};

export const getSeoForPath = (pathname) => {
  if (isNoIndexRoute(pathname)) {
    return {
      title: `${SITE_NAME}`,
      description: homeSeo.description,
      robots: 'noindex, nofollow',
      noCanonical: true,
    };
  }
  return ROUTE_SEO[pathname] || ROUTE_SEO['/'];
};

export const getCanonicalUrl = (pathname) => `${SITE_URL}${pathname === '/' ? '/' : pathname}`;
